/**
 * French Translations (Français)
 */

import type { Translations } from '../types';

export const fr: Translations = {
  language: 'fr',
  languageName: 'Français',

  common: {
    demo: 'DÉMO',
    privacy: 'Confidentialité',
    terms: 'Conditions',
  },

  privacy: {
    title: 'Politique de Confidentialité',
    whatWeStore: 'Ce que nous stockons',
    whatWeStoreDesc:
      "Nous traitons temporairement la géométrie faciale pour l'estimation de l'âge. Aucune image ni modèle biométrique n'est conservé après la vérification.",
    whatWeDont: 'Ce que nous ne stockons pas',
    whatWeDontDesc:
      'Nous ne stockons ni photos, ni vidéos, ni identifiants biométriques. Vos données faciales sont traitées dans le navigateur et supprimées immédiatement.',
    whyWeUseIt: "Pourquoi nous l'utilisons",
    whyWeUseItDesc:
      "L'estimation de l'âge aide les sites web à respecter les réglementations sur la restriction d'âge sans exiger de documents personnels.",
    readFull: 'Lisez notre',
    privacyPolicyLink: 'Politique de Confidentialité',
  },

  theme: {
    system: 'Système',
    light: 'Clair',
    dark: 'Sombre',
  },

  face: {
    noFace: 'Aucun visage détecté',
    tooFar: 'Visage trop loin',
    tooClose: 'Visage trop proche',
    offCenter: 'Visage décentré',
    poorLighting: 'Éclairage insuffisant',
    good: 'Analyse en cours, attendez...',
    holdStill: 'Analyse en cours, attendez...',
    holdStillCountdown: 'Ne bougez pas... {count}',
    processing: 'Traitement...',
    looking: 'Aucun visage détecté',
    centerFace: 'Visage décentré',
    moveCloser: 'Visage trop loin',
    moveBack: 'Visage trop proche',
    keepInPosition: 'Gardez la position',
    perfect: 'Parfait',
    centerInFrame: 'Placez votre visage dans le cadre',
    outsideOval: "Visage hors de l'ovale",
  },

  liveness: {
    blink: {
      title: 'Clignez des yeux',
      hint: 'Clignez lentement une fois',
    },
    smile: {
      title: 'Souriez',
      hint: 'Montrez un sourire naturel',
    },
    lookLeft: {
      title: 'Tournez à gauche',
      hint: 'Tournez lentement la tête vers la gauche',
    },
    lookRight: {
      title: 'Tournez à droite',
      hint: 'Tournez lentement la tête vers la droite',
    },
    openMouth: {
      title: 'Ouvrez la bouche',
      hint: 'Ouvrez grand la bouche',
    },
    lookUp: {
      title: 'Regardez en haut',
      hint: 'Inclinez la tête vers le haut',
    },
    lookDown: {
      title: 'Regardez en bas',
      hint: 'Inclinez la tête vers le bas',
    },
    raiseBrows: {
      title: 'Levez les sourcils',
      hint: 'Levez vos sourcils',
    },
    improveLighting: {
      title: "Améliorez l'éclairage",
      hint: 'Déplacez-vous vers un endroit plus lumineux pour continuer',
    },
  },

  document: {
    selectType: 'Sélectionnez votre type de document',
    captureFront: 'Capturez le recto du document',
    captureBack: 'Capturez le verso du document',
    processing: 'Traitement de votre document...',
    tips: {
      goodLighting: 'Assurez un bon éclairage',
      flatSurface: 'Placez le document sur une surface plane',
      allCorners: 'Assurez-vous que tous les coins sont visibles',
      noGlare: 'Évitez les reflets et les ombres',
    },
    types: {
      passport: 'Passeport',
      idCard: "Carte d'identité",
      driversLicense: 'Permis de conduire',
    },
  },

  ui: {
    continue: 'Continuer',
    next: 'Suivant',
    retry: 'Réessayer',
    startOver: 'Recommencer',
    cancel: 'Annuler',
    success: "Contrôle d'Âge Réussi",
    failure: "Contrôle d'Âge Échoué",
    loading: 'Chargement...',
    error: "Une erreur s'est produite",
    permissionRequired: 'Autorisation requise',
    cameraPermission: "L'accès à la caméra est requis",
    goBack: 'Retour',
    switchCamera: 'Changer de caméra',
    completeVerification: 'Terminer',
    continueToId: "Continuer vers le Contrôle d'ID",
  },

  errors: {
    cameraNotFound: 'Aucune caméra trouvée sur cet appareil',
    cameraPermissionDenied: "L'autorisation de la caméra a été refusée",
    cameraInUse: 'La caméra est utilisée par une autre application',
    cameraNotSupported: 'La caméra ne prend pas en charge les paramètres requis',
    cameraSecurityError: 'La caméra nécessite une connexion HTTPS',
    networkError: 'Erreur réseau. Veuillez vérifier votre connexion.',
    timeout: 'La requête a expiré. Veuillez réessayer.',
    invalidApiKey: 'Clé API invalide',
    sessionExpired: 'Session expirée. Veuillez recommencer.',
    verificationFailed: 'Contrôle échoué. Veuillez réessayer.',
    unknownError: "Une erreur inattendue s'est produite",
    cameraAccessDeniedDetailed: "Veuillez autoriser l'accès à la caméra pour continuer.",
    cameraNotFoundDetailed: 'Aucune caméra détectée. Veuillez connecter une caméra et réessayer.',
    cameraInUseDetailed:
      'La caméra est utilisée par une autre application. Veuillez la fermer et réessayer.',
    cameraOverconstrained:
      'La caméra ne prend pas en charge la résolution demandée. Essai avec les paramètres par défaut...',
    networkTimeout:
      'La requête a pris trop de temps. Veuillez vérifier votre connexion et réessayer.',
    networkOffline: 'Vous semblez être hors ligne. Veuillez vérifier votre connexion internet.',
    apiGenericError: "Quelque chose s'est mal passé. Veuillez réessayer.",
    faceNotDetected: 'Veuillez positionner votre visage dans le cadre.',
    livenessFailed: 'Vérification de vivacité échouée. Veuillez réessayer.',
    documentCaptureFailed:
      "Impossible de capturer le document. Veuillez vous assurer d'avoir un bon éclairage et réessayer.",
    initializationFailed: 'Échec du chargement. Veuillez rafraîchir et réessayer.',
    modelLoadFailed: 'Échec du chargement des composants requis. Veuillez rafraîchir et réessayer.',
    unexpectedError: "Une erreur inattendue s'est produite. Veuillez réessayer.",
    configError: 'Erreur de configuration. Veuillez contacter le support.',
  },

  screens: {
    welcome: {
      title: "Vérification d'Âge 18+",
      subtitle: "Vérification d'Âge Anonyme",
      startButton: 'Commencer',
      secureNote: 'Sécurisé et Chiffré',
      ageTechBy: "Technologie d'âge par AppBouncer",
      waitingCamera: "En attente de l'accès à la caméra",
      allowCamera: "Autoriser l'Accès à la Caméra",
      noBiometric: 'Aucune donnée biométrique stockée',
      loading: 'Chargement...',
      loadingRequirements: 'Chargement des exigences...',
    },
    permissions: {
      title: 'Chargement ...',
      description: "Veuillez autoriser l'accès à la caméra lorsque demandé",
      allowButton: "Autoriser l'Accès à la Caméra",
      privacyNote: 'Vos données sont chiffrées et sécurisées',
      goodLighting: 'Bon Éclairage !',
      goodLightingHint: 'Assurez-vous que votre visage est bien éclairé',
      getReady: 'Préparez-vous',
      removeCoverings: 'Retirez les Couvre-visages',
      removeGlasses: 'Retirez les Lunettes',
    },
    facePosition: {
      title: 'Positionnez Votre Visage',
      hint: 'Centrez votre visage',
      step: 'Étape',
      stepOf: 'Étape {current} sur {total}',
      lookingForFace: 'Recherche du visage...',
    },
    livenessCheck: {
      title: 'Vérification de Vivacité',
      followInstructions: 'Suivez les instructions',
      preparing: 'Préparation...',
      complete: 'Vivacité Vérifiée !',
      actionAutoDetected: "L'action sera détectée automatiquement",
      failed: 'Vérification de Vivacité Échouée',
      failedDescription:
        "Nous n'avons pas pu vérifier votre vivacité. Veuillez vous assurer d'avoir un bon éclairage, regardez directement la caméra et suivez les instructions à l'écran.",
      retryButton: 'Réessayer le Contrôle de Vivacité',
      restartButton: 'Recommencer',
    },
    ageEstimation: {
      title: 'Restez Immobile',
      analyzing: 'Analyse en cours...',
      analyzingProgress: 'Analyse du visage {current} sur {total}...',
      estimatedAge: 'Âge Estimé',
      holdStill: 'Restez Immobile',
      verified: 'Âge Vérifié',
      verificationUncertain: 'Âge Incertain',
      estimationComplete: "Contrôle d'âge terminé",
      confirmedAge: 'Confirmé {minAge}+ ans',
      couldNotConfirm: "Impossible de confirmer l'âge {minAge}+",
      ageConfirmed: 'Âge Confirmé',
      aboveMinimumAge: "Vous remplissez le critère d'âge minimum",
      belowMinimumAge: "En-dessous de l'âge minimum",
      documentRequired: 'Document Requis',
      uploadDocumentMessage:
        "Veuillez télécharger une pièce d'identité valide pour vérifier votre âge",
      idHint: "Carte d'identité, passeport ou permis de conduire",
      confidenceLevels: "Confiance par Seuil d'Âge",
      confidence: 'confiance',
      years: 'ans',
      continueToId: "Continuer vers le Contrôle d'ID",
      demoComplete: 'Mode démo terminé. En production, vous seriez redirigé.',
      improveLighting: "Améliorer l'Éclairage",
      improveLightingHint: 'Déplacez-vous vers un endroit plus lumineux pour des résultats précis',
    },
    documentSelect: {
      title: 'Sélectionner le Type de Document',
      description: "Choisissez le type de document d'identité que vous allez scanner",
      idCard: "Carte d'Identité Nationale",
      idCardHint: 'ID délivrée par le gouvernement',
      passport: 'Passeport',
      passportHint: 'Page photo uniquement',
      driversLicense: 'Permis de Conduire',
      driversLicenseHint: 'Permis de conduire valide',
    },
    captureMode: {
      captureFrontSide: 'Capturer le Recto',
      captureBackSide: 'Capturer le Verso',
      chooseMethod: 'Choisissez comment capturer votre document',
      useCamera: 'Utiliser la Caméra',
      useCameraHint: 'Prenez une photo de votre document',
      uploadFile: 'Télécharger un Fichier',
      uploadFileHint: 'Sélectionner depuis votre appareil',
    },
    documentCamera: {
      frontSide: 'Recto',
      backSide: 'Verso',
      positionInFrame: 'Positionnez le document dans le cadre',
      scanFront: 'Scanner le Recto du Document',
      scanBack: 'Scanner le Verso du Document',
      keepFlat: 'Gardez le document à plat et bien éclairé',
      capturePhoto: 'Capturer la Photo',
    },
    documentPreview: {
      frontPreview: 'Aperçu du Recto',
      backPreview: 'Aperçu du Verso',
      lookingGood: 'Parfait !',
      checkDetails: 'Assurez-vous que tous les détails sont clairement visibles',
      imageQuality: "Qualité de l'image",
      qualityGood: 'Bonne',
      usePhoto: 'Utiliser Cette Photo',
      retakePhoto: 'Reprendre la Photo',
    },
    documentCapture: {
      captureFront: 'Capturer le Recto',
      captureBack: 'Capturer le Verso',
      alignDocument: 'Alignez le document dans le cadre',
      captureButton: 'Capturer',
      retakeButton: 'Reprendre',
      confirmButton: 'Confirmer',
      frontSide: 'Recto',
      backSide: 'Verso',
      positionDocument: 'Positionnez votre document dans le cadre',
      alignWithFrame: 'Alignez le document avec le cadre',
      positionFrontId: "Positionnez le recto de l'ID",
      positionBackId: "Positionnez le verso de l'ID",
      scanFrontId: "Scanner le Recto de l'ID",
      scanBackId: "Scanner le Verso de l'ID",
      positionWithinFrame: 'Positionnez le recto dans le cadre',
      flipAndScan: 'Retournez votre ID et scannez le verso',
      goodLight: 'Bon éclairage',
      flatSurface: 'Surface plane',
      frontCaptured: 'Recto capturé',
      captureIdCard: "Capturer la Carte d'Identité",
      capturePassport: 'Capturer le Passeport',
      captureDriversLicense: 'Capturer le Permis de Conduire',
    },
    processing: {
      title: 'Traitement',
      description: 'Veuillez patienter pendant le traitement',
      analyzingDocument: 'Analyse du document...',
      almostDone: 'Presque terminé...',
      faceVerified: 'Visage vérifié',
      livenessConfirmed: 'Vivacité confirmée',
      processingDocuments: 'Traitement des documents...',
    },
    success: {
      title: "Vérification d'Âge Réussie",
      description: "Vérification d'âge et de vie terminée",
      redirecting: 'Redirection...',
      summary: 'Résumé',
      faceMatch: 'Correspondance du Visage',
      liveness: 'Vivacité',
      age: 'Âge',
      idDocument: "Document d'Identité",
      pass: 'Réussi',
      fail: 'Échoué',
      valid: 'Valide',
      notApplicable: 'N/A',
      done: 'Terminé',
      feedbackTitle: 'Veuillez évaluer votre expérience',
      feedbackPlaceholder: 'Dites-nous comment nous améliorer...',
      skip: 'Passer',
      submit: 'Soumettre',
    },
    failure: {
      title: "Contrôle d'Âge Échoué",
      description: "Nous n'avons pas pu compléter le contrôle. Veuillez réessayer.",
      tryAgainButton: 'Réessayer',
      leaveButton: 'Quitter',
      attemptsLeft: '{{count}} tentative(s) restante(s)',
      noAttemptsLeft: 'Plus de tentatives disponibles',
    },
    error: {
      title: 'Configuration Requise',
      description:
        "Certains paramètres requis manquent dans l'URL. Veuillez contacter l'administrateur du site.",
      missingParams: 'Paramètres Manquants',
      requiredParams: 'Paramètres Requis',
      technicalDetails: 'Détails Techniques',
      urlMustInclude: "L'URL doit inclure ces paramètres de requête :",
    },
    underage: {
      title: "Condition d'Âge Non Remplie",
      description: 'Vous devez avoir au moins {{minAge}} ans pour accéder à ce service.',
    },
    consent: {
      title: "Aidez à Améliorer le Contrôle d'Âge",
      description:
        "Permettez à AppBouncer de collecter une petite quantité de données d'utilisation anonymisées (comme les captures de flux) pour améliorer la vérification de l'âge et les vérifications de secours. Vos données ne sont jamais vendues ni fournies à des tiers.",
      allowButton: 'Autoriser',
    },
    documentConsent: {
      title: 'Document Requis',
      description:
        "Pour compléter le contrôle, nous devons vérifier votre âge à l'aide d'un document officiel. Consentez-vous à télécharger votre pièce d'identité ?",
      allowButton: 'Continuer avec le Document',
      declineButton: 'Refuser',
      privacyNote: 'Votre document sera traité de manière sécurisée et supprimé après le contrôle.',
    },
    selfDeclaration: {
      title: 'Votre Date de Naissance',
      description: 'Veuillez entrer votre date de naissance pour continuer',
      day: 'Jour',
      month: 'Mois',
      year: 'Année',
      yourAge: 'Votre âge :',
      years: 'ans',
      confirmButton: 'Continuer',
      months: {
        january: 'Janvier',
        february: 'Février',
        march: 'Mars',
        april: 'Avril',
        may: 'Mai',
        june: 'Juin',
        july: 'Juillet',
        august: 'Août',
        september: 'Septembre',
        october: 'Octobre',
        november: 'Novembre',
        december: 'Décembre',
      },
    },
  },

  lighting: {
    warning: "Problème d'Éclairage",
    adjust: "Ajustez l'éclairage",
    lowLight: 'Faible luminosité détectée',
    tooBright: 'Trop lumineux',
    poorContrast: 'Contraste insuffisant',
    improveLighting: 'Trouvez un meilleur éclairage',
    moveToLighter: 'Déplacez-vous vers un endroit plus lumineux',
    videoNotReady: 'Vidéo non prête',
  },

  steps: {
    faceDetection: 'Détection du Visage',
    liveness: 'Vérification de Vivacité',
    age: "Contrôle d'Âge",
    documentScan: 'Numérisation de Document',
    selfDeclaration: 'Auto-déclaration',
  },

  status: {
    cameraStarted: 'Caméra démarrée',
    cameraReady: 'Caméra prête',
    cameraAccessDenied: 'Accès à la caméra refusé',
    cameraError: 'Erreur de caméra',
    cameraNotAvailable: 'Caméra non disponible',
    noFaceDetected: 'Aucun visage détecté',
    livenessVerified: 'Vivacité vérifiée',
    livenessCheckFailed: 'Vérification de vivacité échouée',
    verifyingAge: "Vérification de l'âge...",
    processingDocument: 'Traitement du document...',
    documentProcessed: 'Document traité avec succès',
    verificationCancelled: 'Contrôle annulé',
    improveLightingForAge: "Améliorez l'éclairage pour des résultats précis",
    getReady: 'Préparez-vous',
    preparingLiveness: 'Préparation de la vérification de vivacité...',
    uploadingSelfie: 'Téléchargement des images selfie...',
    preparingUpload: 'Préparation du téléchargement...',
    uploadingFront: 'Téléchargement du document recto...',
    uploadingBack: 'Téléchargement du document verso...',
    processingDocuments: 'Traitement des documents...',
    documentSubmissionFailed: "Échec de l'envoi du document. Veuillez réessayer.",
    selectJpegOrPng: 'Veuillez sélectionner une image JPEG ou PNG',
    fileTooLarge: 'Fichier trop volumineux. Maximum 5 Mo autorisé.',
    errorReadingFile: 'Erreur de lecture du fichier',
    frontDocumentRequired: 'Image du document recto requise',
    verificationFailedClose: 'Contrôle échoué. Vous serez redirigé.',
    verificationCompleteClose: 'Contrôle terminé ! Vous serez redirigé.',
    analyzingFace: 'Analyse du visage...',
  },
};
